/**
 * @module AliTableAnalytics/utils/applyColumnConditionalFormat
 * @description 建列阶段 — 把 chartConfig 里的条件格式挂到每一列（第 3 层）。
 *
 * 【时机】表格 columns 生成后、渲染前，只执行一次（非每个单元格）。
 *
 * 【输入】conditional_format 快照，结构与保存到 customizeSetting / chart_detail 一致：
 *   {
 *     table_bar_percentage?: [...],   // 条形条件
 *     table_numerical_values?: [...], // 数值条件（含 rules）
 *   }
 *
 * 【输出】在 TAliTableColumn 上写入：
 *   - barPercentConfig / numericalFormatConfig
 *   - cellType → 决定 AliTableCell 用哪个组件渲染
 *
 * 【平表入口】utils.tsx 的 setColumnConditionalFormatting → applyConditionalFormatToColumns
 * 【交叉表】TableAnalytics/tableColumnsBuilder 内另有类似逻辑（第 5 步讲）
 */

import type { INumericalFormatRule } from '@dbl/core/chartEditor';

import {
  AliTableCellTypeEnum,
  type TAliTableColumn,
  type TBarPercentConfig,
} from '../types';

/** 条形配置行：与 TBarPercentConfig 一致，交叉表可能带 allowList */
type TBarPercentageRow = TBarPercentConfig & {
  allowList?: string[];
};

/** 数值配置行：按 metric（或 id）绑定到某一指标列 */
type TNumericalValueRow = {
  id?: string;
  metric?: string;
  table_code?: string;
  rules?: INumericalFormatRule[];
};

/** 从 chartConfig.conditionalFormat 传入的整包数据 */
export type TConditionalFormattingInput = {
  table_bar_percentage?: TBarPercentageRow[];
  table_numerical_values?: TNumericalValueRow[];
};

/** 未传 table_code 时默认 t0，与列上的 tableCode 对齐 */
const normalizeTableCode = (code?: string): string => code || 't0';

// ---------------------------------------------------------------------------
// 查找：当前列对应哪一条条形 / 数值配置
// ---------------------------------------------------------------------------

/**
 * 条形：用 table_code + metric(originName) 精确匹配一条 table_bar_percentage 项。
 */
export const findBarPercentConfigForColumn = (
  list: TBarPercentageRow[] | undefined,
  tableCode: string | undefined,
  originName: string | undefined,
): TBarPercentageRow | undefined => {
  if (!list?.length || !originName) {
    return undefined;
  }
  const rowTable = normalizeTableCode(tableCode);
  return list.find(
    item =>
      normalizeTableCode(item.table_code) === rowTable &&
      item.metric === originName,
  );
};

/**
 * 数值：同样按 table_code 过滤；列键用 metric，缺省时用 id（与面板保存字段一致）。
 */
export const findNumericalConfigForColumn = (
  list: TNumericalValueRow[] | undefined,
  tableCode: string | undefined,
  originName: string | undefined,
): TNumericalValueRow | undefined => {
  if (!list?.length || !originName) {
    return undefined;
  }
  const rowTable = normalizeTableCode(tableCode);
  return list.find(item => {
    if (normalizeTableCode(item.table_code) !== rowTable) {
      return false;
    }
    const key = item.metric ?? item.id;
    return key === originName;
  });
};

// ---------------------------------------------------------------------------
// 挂载 + 决定 cellType（三种互斥/叠加形态）
// ---------------------------------------------------------------------------

/**
 * 为单列叠加条件格式并设置 cellType。
 *
 * cellType 决策表（同一列可同时有条形 + 数值配置）：
 * | 条形 | 数值 rules | cellType              | 渲染组件              |
 * |------|------------|------------------------|------------------------|
 * | ✓    | ✓          | BarPercentNumerical    | 条背景 + 数值色/图标   |
 * | ✓    | ✗          | BarPercent               | 仅条背景               |
 * | ✗    | ✓          | NumericalCondition       | 仅数值色/图标          |
 * | ✗    | ✗          | （保持原 cellType）    | 普通单元格             |
 */
export const applyConditionalFormatToColumn = (
  column: TAliTableColumn,
  conditionalFormatting?: TConditionalFormattingInput | null,
): TAliTableColumn => {
  if (!conditionalFormatting) {
    return column;
  }

  const barItem = findBarPercentConfigForColumn(
    conditionalFormatting.table_bar_percentage,
    column.tableCode,
    column.originName,
  );
  const numericalItem = findNumericalConfigForColumn(
    conditionalFormatting.table_numerical_values,
    column.tableCode,
    column.originName,
  );

  if (!barItem && !numericalItem) {
    return column;
  }

  const next: TAliTableColumn = { ...column };

  if (barItem) {
    next.barPercentConfig = barItem;
  }
  if (numericalItem?.rules?.length) {
    next.numericalFormatConfig = { rules: numericalItem.rules };
  }

  if (barItem && numericalItem?.rules?.length) {
    next.cellType = AliTableCellTypeEnum.BarPercentNumerical;
  } else if (barItem) {
    next.cellType = AliTableCellTypeEnum.BarPercent;
  } else if (numericalItem?.rules?.length) {
    next.cellType = AliTableCellTypeEnum.NumericalCondition;
  }

  return next;
};

/** 平表：对 columns 数组 map 一遍 */
export const applyConditionalFormatToColumns = (
  columns: TAliTableColumn[],
  conditionalFormatting?: TConditionalFormattingInput | null,
): TAliTableColumn[] => {
  if (!conditionalFormatting) {
    return columns;
  }
  return columns.map(column =>
    applyConditionalFormatToColumn(column, conditionalFormatting),
  );
};
